import axios from '../../../helpers/axios';
import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { QUERY_KEYS } from '../../../queries/query.keys';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { notificationState } from '../../../recoil/notification.atom';
import { ICustomerAddress } from '../interfaces/customerAddress';

export const useMutateCustomerAddress = (customerId: string) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const setNotification = useSetRecoilState(notificationState);

    const create = useMutation<AxiosResponse, AxiosError, ICustomerAddress>(
        (data: ICustomerAddress) => axios.post(`customer-address`, data),
        {
            onSuccess: async (res) => {
                setNotification({
                    open: true,
                    severity: 'success',
                    message: t('CUSTOMER_ADDRESSES.CREATE_SUCCESS')
                });
                return res;
            },
            onError: (err) => {
                console.error(err.request.response);
                setNotification({ open: true, severity: 'error', message: t('CUSTOMER_ADDRESSES.CREATE_ERROR') });
            },
            onSettled: async () => {
                await queryClient.invalidateQueries([QUERY_KEYS.CUSTOMER_ADDRESS_LIST, customerId]);
            }
        }
    );

    const update = useMutation<AxiosResponse, AxiosError, ICustomerAddress>(
        (data: ICustomerAddress) => axios.put(`customer-address/${data.id}`, data),
        {
            onSuccess: async (res) =>
                setNotification({
                    open: true,
                    severity: 'success',
                    message: t('CUSTOMER_ADDRESSES.UPDATE_SUCCESS')
                }),
            onError: (err) => {
                console.error(err.request.response);
                setNotification({ open: true, severity: 'error', message: t('CUSTOMER_ADDRESSES.UPDATE_ERROR') });
            },
            onSettled: async () => {
                await queryClient.invalidateQueries([QUERY_KEYS.CUSTOMER_ADDRESS_LIST, customerId]);
            }
        }
    );

    const remove = useMutation<AxiosResponse, AxiosError, ICustomerAddress>(
        (data: ICustomerAddress) => axios.delete(`customer-address/${data.id}`),
        {
            onSuccess: async (res) =>
                setNotification({
                    open: true,
                    severity: 'success',
                    message: t('CUSTOMER_ADDRESSES.DELETE_SUCCESS')
                }),
            onError: (err) => {
                console.error(err.request.response);
                setNotification({ open: true, severity: 'error', message: t('CUSTOMER_ADDRESSES.DELETE_ERROR') });
            },
            onSettled: async () => {
                await queryClient.invalidateQueries([QUERY_KEYS.CUSTOMER_ADDRESS_LIST, customerId]);
            }
        }
    );

    return { update, create, remove };
};
